/**
 * 时间胶囊（ADR-0011，design §11.6）前端这边的规矩：现在处在哪个阶段、倒计时怎么说、解封的日子怎么挑。
 * 能不能看里面由服务端决定（拿到的 CapsuleDto 里有没有 letter、photo）。
 */
import { addDays, daysBetween, isLeapYear, parseLocalDate, type CapsuleDto, type LocalDate } from '@star-sea/shared';

/**
 * - sealed：静默封印（离解封还有一天以上）：星茧慢慢呼吸
 * - warming：临界预热（最后 24 小时）：表面浮出发光的裂纹，呼吸变成心跳一样的短促脉冲
 * - ready：时间到了、你还没亲手拆：充满金光、裂缝全开，等你长按
 * - opened：你拆开过（秘密惊喜的封存的人不用拆，Ta 拆开过就算）：一颗带永久星环的星
 */
export type CapsulePhase = 'sealed' | 'warming' | 'ready' | 'opened';

const HOUR = 3_600_000, DAY = 24 * HOUR;

export function phaseOf(c: CapsuleDto, me: string, now: number): CapsulePhase {
  if (c.openedBy.includes(me)) return 'opened';
  // 秘密惊喜的封存的人一直看得到里面：Ta 拆开了，惊喜就送到了
  if (c.kind === 'surprise' && c.createdBy === me && c.openedBy.length) return 'opened';
  const left = Date.parse(c.unlockAt) - now;
  if (left <= 0) return 'ready';
  return left <= DAY ? 'warming' : 'sealed';
}

/** 「还有 128 天」「还有 5 小时」「还有 12 分钟」；已经到了是空字符串 */
export function countdown(unlockAt: string, now: number): string {
  const left = Date.parse(unlockAt) - now;
  if (left <= 0) return '';
  if (left > DAY) return `还有 ${Math.ceil(left / DAY).toLocaleString('zh-CN')} 天`;
  if (left > HOUR) return `还有 ${Math.ceil(left / HOUR)} 小时`;
  return `还有 ${Math.max(1, Math.ceil(left / 60_000))} 分钟`;
}

/** 「2027.02.14」 */
export function dotted(date: LocalDate): string {
  const { y, m, d } = parseLocalDate(date);
  return `${y}.${String(m).padStart(2, '0')}.${String(d).padStart(2, '0')}`;
}

/** 共同契约被偷看过的印记：「已于 2026.10 被创建者偷偷翻阅过 1 次」 */
export function peekMark(c: CapsuleDto): string {
  if (!c.peekCount || !c.peekedAt) return '';
  const d = new Date(c.peekedAt);
  return `已于 ${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')} 被创建者偷偷翻阅过 ${c.peekCount} 次`;
}

/** 这台设备那里，那一天的零点（解封的时刻） */
export function midnightOf(date: LocalDate): string {
  const { y, m, d } = parseLocalDate(date);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

export interface UnlockPreset {
  label: string;
  date: LocalDate;
}

/** 下一个 m 月 d 日（今天不算）；2 月 29 日在平年用 2 月 28 日 */
function nextMonthDay(today: LocalDate, m: number, d: number): LocalDate {
  const t = parseLocalDate(today);
  for (let y = t.y; ; y++) {
    const day = m === 2 && d === 29 && !isLeapYear(y) ? 28 : d;
    const date = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}` as LocalDate;
    if (daysBetween(today, date) > 0) return date;
  }
}

const NUMERALS = '一两三四五六七八九十';

/** 解封日子的几个现成选项：一段时间以后，和接下来的几个纪念日（按日子先后） */
export function unlockPresets(since: LocalDate, today: LocalDate): UnlockPreset[] {
  const s = parseLocalDate(since), t = parseLocalDate(today);
  const anniv = nextMonthDay(today, s.m, s.d);
  const years = parseLocalDate(anniv).y - s.y;
  // 明年的今天（今天是 2 月 29 日的话是明年 2 月 28 日）
  const nextYear = `${t.y + 1}-${String(t.m).padStart(2, '0')}-${String(t.m === 2 && t.d === 29 ? 28 : t.d).padStart(2, '0')}` as LocalDate;
  // 撞在同一天时留前面的：纪念日的名字比「一年以后」更有意思
  const out: UnlockPreset[] = [
    { label: years >= 1 && years <= NUMERALS.length ? `在一起${NUMERALS.charAt(years - 1)}周年` : `在一起 ${years} 周年`, date: anniv },
    { label: '情人节', date: nextMonthDay(today, 2, 14) },
    { label: '5 月 20 日', date: nextMonthDay(today, 5, 20) },
    { label: '新年零点', date: nextMonthDay(today, 1, 1) },
    { label: '100 天以后', date: addDays(today, 100) },
    { label: '一年以后', date: nextYear },
  ];
  const seen = new Set<string>();
  return out.filter((p) => !seen.has(p.date) && seen.add(p.date)).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
